import { appdata } from "./AppData";
import { Dist } from "./Dist"
import { v4 as uuidv4 } from 'uuid';
import Graphics from "./Graphics";
import * as THREE from 'three'

export enum memberKind {
  solidRect = 'Solid Rectangular',
  hollowRect = 'Hollow Rectangular',
  solidRound = 'Solid Round',
  hollowRound = 'Hollow Round',
  coldFormed = 'Cold-Formed',
  wideFlange = 'Wide Flange',
  metalDeck = 'Metal Deck',
  zee = 'Zee',
}

export type MemberKindInfo = {
  name: string
  requiredInputs: string[]
}

export const memberKindInfos: MemberKindInfo[] = [
  {name: memberKind.solidRect, requiredInputs: ['materialGuid', 'depth', 'width']},
  {name: memberKind.hollowRect, requiredInputs: ['materialGuid', 'depth', 'width', 'thickness']},
  {name: memberKind.coldFormed, requiredInputs: ['materialGuid', 'depth', 'width', 'thickness', 'lip']},
  {name: memberKind.wideFlange, requiredInputs: ['materialGuid', 'depth', 'width', 'webThickness', 'flangeThickness']},
  {name: memberKind.metalDeck, requiredInputs: ['materialGuid', 'depth', 'width', 'thickness', 'topFlute', 'bottomFlute']},
  {name: memberKind.solidRound, requiredInputs: ['materialGuid', 'depth']},
  {name: memberKind.hollowRound, requiredInputs: ['materialGuid', 'depth', 'thickness']},
  {name: memberKind.zee, requiredInputs: ['materialGuid', 'depth', 'width', 'thickness']},
]

export type AMemberInfo = {
  guid: string
  kind: string
  name: string
  materialGuid: string
  depth: number //inches
  width: number //inches
  thickness: number //inches
  lip: number //inches
  webThickness: number //inches
  flangeThickness: number //inches
  area: number //square inches cross section
  weight: number //pounds per linear foot
  topFlute: number //inches
  bottomFlute: number //inches
  notes: string
}

export const defaultAMemberInfo: AMemberInfo = {
  guid: '',
  kind: memberKind.solidRect,
  name: '',
  materialGuid: '',
  depth: 0,
  width: 0,
  thickness: 0,
  lip: 0,
  webThickness: 0,
  flangeThickness: 0,
  area: 0,
  weight: 0,
  topFlute: 0,
  bottomFlute: 0,
  notes: ''
}

/**Generates a member name based on material and shape. */
export function generateMemberName(info: AMemberInfo): string {
  let n = ''
  const material = appdata.getMaterial(info.materialGuid)
    switch (info.kind){
      case memberKind.solidRect:
        n = `${material.name}: ${info.depth.toFixed(2)}"x${info.width.toFixed(2)}"`
        break
      case memberKind.hollowRect:
        n = `${material.name}: ${info.depth.toFixed(2)}"x${info.width.toFixed(2)}" (${info.thickness.toFixed(3)}" thick)`
       break
      case memberKind.solidRound:
        n = `${material.name}: ${info.depth.toFixed(2)}" Diameter`
        break
      case memberKind.hollowRound:
        n = `${material.name}: ${info.depth.toFixed(2)}" Diameter (${info.thickness.toFixed(3)}" thick)`
        break
      case memberKind.coldFormed:
        n = `${material.name}: ${info.depth.toFixed(2)}"x${info.width.toFixed(2)}" (${info.thickness.toFixed(3)}" thick, ${info.lip.toFixed(2)}" lip)`
        break
      case memberKind.wideFlange:
        n = `${material.name}: ${info.depth.toFixed(2)}"x${info.width.toFixed(2)}" Wide Flange (${info.webThickness.toFixed(3)}" web, ${info.flangeThickness.toFixed(2)}" flange)`
        break
      case memberKind.metalDeck:
        n = `${material.name}: ${info.depth.toFixed(2)}" (W:${info.width.toFixed(2)}" T:${info.topFlute.toFixed(2)}" B:${info.bottomFlute.toFixed(2)}") ${info.thickness.toFixed(4)}" thick`
        break
      case memberKind.zee:
        n = `${material.name} Zee: ${info.depth.toFixed(2)}"x${info.width.toFixed(2)}" (${info.thickness.toFixed(3)}" thick)`
        break
      default:
        break
    }
  return n
}

export class AMember {
  guid: string
  kind: string
  protected _name: string
  materialGuid: string
  depth: Dist
  width: Dist
  thickness: Dist
  lip: Dist
  webThickness: Dist
  flangeThickness: Dist
  protected _area: number //square inches cross section
  protected _weight: number //pounds per linear foot
  topFlute: Dist
  bottomFlute: Dist
  notes: string
  shape: THREE.Shape

  constructor (info: AMemberInfo = defaultAMemberInfo){
    this.guid = info.guid === '' ? uuidv4() : info.guid
    this.kind = info.kind
    this._name = info.name ? info.name : ''
    this.materialGuid = info.materialGuid ? info.materialGuid : ''
    this.depth = info.depth? Dist.inches(info.depth) : Dist.zero
    this.width = info.width ? Dist.inches(info.width) : Dist.zero
    this.thickness = info.thickness ? Dist.inches(info.thickness) : Dist.zero
    this.lip = info.lip ? Dist.inches(info.lip) : Dist.zero
    this.webThickness = info.webThickness ? Dist.inches(info.webThickness) : Dist.zero
    this.flangeThickness = info.flangeThickness ? Dist.inches(info.flangeThickness) : Dist.zero
    this._area = info.area ? info.area : 0
    this._weight = info.weight ? info.weight : 0
    this.topFlute = info.topFlute ? Dist.inches(info.topFlute) : Dist.zero
    this.bottomFlute = info.bottomFlute ? Dist.inches(info.bottomFlute) : Dist.zero
    this.notes = info.notes ? info.notes : ''
    this.shape = new THREE.Shape()
    this.regenShape()
  }

  /**regenerate the member shape from its dimension properties */
  regenShape(){
    switch (this.kind){
      case memberKind.solidRect:
        this.shape = Graphics.getMemberSolidShape(this.depth, this.width)
        break
      case memberKind.hollowRect:
        this.shape = Graphics.getMemberHollowShape(this.depth, this.width, this.thickness)
       break
      case memberKind.solidRound:
        this.shape = Graphics.getMemberRoundShape(this.depth)
        break
      case memberKind.hollowRound:
        this.shape = Graphics.getMemberRoundHollowShape(this.depth, this.thickness)
        break
      case memberKind.coldFormed:
        this.shape = Graphics.getMemberColdFormedShape(this.depth, this.width, this.thickness, this.lip)
        break
      case memberKind.wideFlange:
        this.shape = Graphics.getMemberWideFlangeShape(this.depth, this.width, this.webThickness, this.flangeThickness)
        break
      case memberKind.metalDeck:
        this.shape = Graphics.getMemberMetalDeckShape(this.depth, this.width, this.thickness, this.topFlute, this.bottomFlute)
        break
      case memberKind.zee:
        this.shape = Graphics.getMemberZeeShape(this.depth, this.width, this.thickness)
        break
      default:
        break
    }
  }

  /**update the member with info object */
  update(info: AMemberInfo){
    this.kind = info.kind
    this._name = info.name
    this.materialGuid = info.materialGuid
    this.depth = Dist.inches(info.depth)
    this.width = Dist.inches(info.width)
    this.thickness = Dist.inches(info.thickness)
    this.lip = Dist.inches(info.lip)
    this.webThickness = Dist.inches(info.webThickness)
    this.flangeThickness = Dist.inches(info.flangeThickness)
    this._area = info.area
    this._weight = info.weight
    this.topFlute = Dist.inches(info.topFlute)
    this.bottomFlute = Dist.inches(info.bottomFlute)
    this.notes = info.notes
    this.regenShape()
  }

  /**returns member info object */
  get info(): AMemberInfo{
    let info: AMemberInfo = {
      guid: this.guid,
      kind: this.kind,
      name: this._name,
      materialGuid: this.materialGuid,
      depth: this.depth.in,
      width: this.width.in,
      thickness: this.thickness.in,
      lip: this.lip.in,
      webThickness: this.webThickness.in,
      flangeThickness: this.flangeThickness.in,
      area: this._area,
      weight: this._weight,
      topFlute: this.topFlute.in,
      bottomFlute: this.bottomFlute.in,
      notes: this.notes
    }
    return info
  }
  
  /**returns member name*/
  get name(): string{
    if(this._name === ''){
      this._name = generateMemberName(this.info)
    }
    return this._name
  }

  /**returns member's material's category */
  get category(): string{
    return appdata.getMaterial(this.materialGuid).category
  }

  /**returns cross-sectional area in square inches*/
  get area(): number{
    if(this._area > 0){
      return this._area
    }
    let a = 0
    let w = this.width.in
    let d = this.depth.in
    let t = this.thickness.in
    switch (this.kind){
      case memberKind.solidRect:
        a = d * w
        break
      case memberKind.hollowRect:
        a = ( w * d - ((w - 2*t)*(d - 2*t)) ) // outer minus inner rectangle
       break
      case memberKind.solidRound:
        a = Math.PI * (Math.pow( (d/2), 2)) // pi*r^2
        break
      case memberKind.hollowRound:
        const outer = Math.PI * (Math.pow( (d/2), 2)) // pi*r^2
        const inner = Math.PI * (Math.pow( (d/2 - t), 2)) // pi*r^2
        a = outer - inner
        break
      case memberKind.coldFormed:
        const lip = this.lip.in
        //a = (w*d) - ( (w-2*t)*(d-2*t) ) - (t * (d-2*lip)) // solid rectangle minus voids
        a = ( d + 2*w + 2*lip) * t // shape length x thickness
        break
      case memberKind.wideFlange:
        const tf = this.flangeThickness.in
        const tw = this.webThickness.in
        const flangeArea = w * tf
        const webArea = (d - 2*tf) * tw
        a = webArea + 2*flangeArea
        break
      case memberKind.metalDeck:
        const slopeH = Math.abs( (w - this.bottomFlute.in - this.topFlute.in) / 2 )
        const slopeV = this.depth.in
        const transition = Math.sqrt( slopeH^2 + slopeV^2)
        a = ( (this.topFlute.in + this.bottomFlute.in + ( 2 * transition )) * t ) * (12 / w) // area per linear foot of deck profile
        break
      case memberKind.zee:
        a = ( (w + d) * t ) // length of shape x thickness
        break
      default:
        break
    }
    return a
  }

  /**returns cross-sectional area of filled trapezoid in square inches per unit foot of profile length for metal deck fill*/
  get fillArea(): number{
    if(this.kind !== memberKind.metalDeck){
      return 0
    }
    const w = this.width.in
    const d = this.depth.in
    const slopeH = (w - this.bottomFlute.in - this.topFlute.in) / 2
    return ( (this.bottomFlute.in  + slopeH) * d ) * (12 / w)
  }

  /**returns an empty member */
  static getEmpty(): AMember {
    return new AMember({...defaultAMemberInfo, name: '<none>', guid: '0'})
  }

  /**custom method to convert to json*/
  toJSON(): object {
    return this.info
  }

}

